import React from 'react';
import {
  Button,
  Stack,
  TextField,
  Typography,
  Divider,
  MenuItem,
  Autocomplete,
  IconButton,
  Box,
  List,
  ListItem,
  Checkbox,
  FormControlLabel,
  Select,
  InputLabel,
  FormControl,
  InputAdornment,
  Alert,
  Chip,
  FormHelperText,
  Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import { get, post, put, del } from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import { isInternalNoEmail, sanitizeEmail } from '../../utils/customerEmail';
import { ICCT_ORDER_META } from '../../constants/icctWooOrderMeta';

const STATUS_OPTIONS = [
  'pending',
  'processing',
  'completed',
  'cancelled',
  'on-hold',
  'pledge-pending',
  'pledge-completed',
  'refunded',
  'failed' // Custom status (not a standard WooCommerce status, but used internally)
];

/**
 * Full-page order editor (used from /orders/:orderId/edit). No dialogs.
 */
export default function OrderEditView({
  /** Minimal { id } or richer seed; data is always reloaded from API */
  order,
  /** Where "Back" / after-delete navigation goes */
  returnTo = '/orders',
  /** Optional: navigate to post-payment for another order */
  onAddAnotherOrder,
}) {
  const navigate = useNavigate();
  const [saving, setSaving] = React.useState(false);
  // True until the initial order hydration completes. Prevents a Save click from clobbering
  // saved values (e.g. tx_id) with the form's pre-load empty state.
  const [initialLoading, setInitialLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [successMessage, setSuccessMessage] = React.useState('');
  const [savingMessage, setSavingMessage] = React.useState('');
  const [sendingEmail, setSendingEmail] = React.useState(false);
  const [orderNotFoundInWc, setOrderNotFoundInWc] = React.useState(false);
  const [wcErrorMessage, setWcErrorMessage] = React.useState('');
  const [formErrors, setFormErrors] = React.useState({});

  // Detect backend-confirmed "order not found in WooCommerce" (only then show Delete locally)
  const isOrderNotFoundInWc = (err) => {
    const d = err?.data || {};
    const msg = String(err?.message || '') + String(d?.message || '') + String(d?.error || '');
    return (err?.status === 404 && (d?.code === 'ORDER_NOT_FOUND_IN_WC' || /not exist in WooCommerce|Order not found in WooCommerce/i.test(msg)));
  };
  const setOrderNotFoundState = (errorData) => {
    setOrderNotFoundInWc(true);
    setWcErrorMessage(errorData?.message || errorData?.wc_error?.message || '');
    setError(errorData?.message || errorData?.error || 'Order not found in WooCommerce');
  };

  // Editable fields
  const [status, setStatus] = React.useState(order?.status || '');
  const [orderNumber, setOrderNumber] = React.useState(order?.order_number || null); // friendly YYYY-NNNNN, from the loaded local order
  // Seed customer with current order billing summary so it's displayed even before search
  const seedCustomer = order?.customer_id ? {
    id: order.customer_id,
    first_name: order?.billing_first_name || '',
    last_name: order?.billing_last_name || '',
    email: order?.billing_email || '',
  } : null;
  const [customer, setCustomer] = React.useState(seedCustomer);
  const [customerOptions, setCustomerOptions] = React.useState([]);
  const [custLoading, setCustLoading] = React.useState(false);
  const customerDetailsLookup = React.useRef({});

  const [items, setItems] = React.useState(() => {
    const arr = Array.isArray(order?.items) ? order.items : [];
    return arr.map((it, idx) => {
      const meta = Array.isArray(it.meta_data) ? it.meta_data : [];
      const attrLabel = meta
        .filter(m => m && typeof m.key === 'string' && !String(m.key).startsWith('_'))
        .map(m => `${m.display_key || m.key}: ${m.display_value || m.value}`)
        .join(', ');
      return {
      key: `${it.product_id || 'p'}-${it.variation_id || 'v'}-${idx}`,
      id: it.id, // may be undefined in our denorm items; safe to omit
      product_id: it.product_id || null,
      variation_id: it.variation_id || null,
      name: it.name || '',
      sku: it.sku || '',
      quantity: it.quantity || 1,
      total: typeof it.total_num === 'number' ? it.total_num : (typeof it.total === 'number' ? it.total : 0),
        variation_label: attrLabel || it.name || '',
      };
    });
  });

  const [paymentMethod, setPaymentMethod] = React.useState(order?.payment_method || '');
  const [paymentMethods, setPaymentMethods] = React.useState([]);
  const [orderDate, setOrderDate] = React.useState(order?.date_created_gmt ? dayjs(order.date_created_gmt).format('YYYY-MM-DDTHH:mm') : '');
  const [txId, setTxId] = React.useState('');
  const [orderChange, setOrderChange] = React.useState('0.00');

  const [productQuery, setProductQuery] = React.useState('');
  const [productOptions, setProductOptions] = React.useState([]);
  const [prodLoading, setProdLoading] = React.useState(false);

  const [variationOptions, setVariationOptions] = React.useState([]);
  const [variationsByProduct, setVariationsByProduct] = React.useState(new Map()); // Map<product_id, variations[]>
  const [varLoading, setVarLoading] = React.useState(false);
  const [selectedProductForNew, setSelectedProductForNew] = React.useState(null);
  const [selectedVariationForNew, setSelectedVariationForNew] = React.useState(null);
  const [newQty, setNewQty] = React.useState(1);
  const [newTotal, setNewTotal] = React.useState('0.00');
  // Fees (e.g., CC fees, discounts as negative fee)
  const [feeLines, setFeeLines] = React.useState([]);
  const [newFeeName, setNewFeeName] = React.useState('');
  const [newFeeTotal, setNewFeeTotal] = React.useState('0.00');
  const [discountAmt, setDiscountAmt] = React.useState('0.00');
  
  // Track original item data to detect changes (product_id, variation_id, quantity, total)
  const originalItemsRef = React.useRef([]);
  // Track original fee lines to detect changes (name, total)
  const originalFeeLinesRef = React.useRef([]);
  // Track original order date to detect changes
  const originalOrderDateRef = React.useRef('');

  const parsePrice = (v) => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    const num = parseFloat(v);
    return Number.isNaN(num) ? null : num;
  };
  const formatMoney = (v) => (parseFloat(v) || 0).toFixed(2);
  const getChicagoTodayIsoDate = () => {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date());
  };
  const isPaidStatus = (s) => {
    const v = String(s || '').toLowerCase();
    return v === 'completed' || v === 'pledge-completed';
  };

  // Clear transient messages when order id changes
  React.useEffect(() => {
    setError('');
    setSuccessMessage('');
    setSavingMessage('');
    setOrderNotFoundInWc(false);
    setWcErrorMessage('');
  }, [order?.id]);

  // Shared function to load order data (used both on dialog open and after save)
  const loadOrderData = React.useCallback(async (orderId) => {
    if (!orderId) {
      console.warn('[OrderEditDialog] loadOrderData called without orderId');
      return null;
    }

    console.log('[OrderEditDialog] loadOrderData called for order:', orderId);

    let src = null;
    let localComments = [];
    try {
      console.log('[OrderEditDialog] Fetching from local DB:', `/ldb/orders/${orderId}`);
      const localRes = await get(`/ldb/orders/${orderId}`, {});
      console.log('[OrderEditDialog] Local DB response:', {
        hasOrder: !!localRes?.order,
        hasItems: !!localRes?.items,
        itemsCount: localRes?.items?.length || 0,
        hasFeeLines: !!localRes?.fee_lines,
        feeLinesCount: localRes?.fee_lines?.length || 0
      });
      
      if (localRes?.order) {
        // Transform local data to match WooCommerce format
        const localOrder = localRes.order;
        setOrderNumber(localOrder.order_number || localOrder.woo_order_id || null); // friendly # for the header
        const localItems = Array.isArray(localRes.items) ? localRes.items : [];
        const localFeeLines = Array.isArray(localRes.fee_lines) ? localRes.fee_lines : [];
        localComments = Array.isArray(localRes.comments) ? localRes.comments : [];
        const localMetaObj = localOrder.meta_data && typeof localOrder.meta_data === 'object' ? localOrder.meta_data : {};
        const txFromMeta = (k) => {
          const v = localMetaObj[k];
          return v != null && String(v).trim() !== '' ? String(v).trim() : '';
        };
        src = {
          id: localOrder.id,
          status: localOrder.status,
          customer_id: localOrder.customer_id || 0,
          payment_method: localOrder.payment_method || '',
          transaction_id:
            txFromMeta('transaction_id') ||
            txFromMeta('_transaction_id') ||
            txFromMeta('tx_id') ||
            txFromMeta('check_number') ||
            '',
          billing: {
            first_name: localOrder.billing_first_name || '',
            last_name: localOrder.billing_last_name || '',
            address_1: localOrder.billing_address_1 || '',
            address_2: localOrder.billing_address_2 || '',
            city: localOrder.billing_city || '',
            state: localOrder.billing_state || '',
            postcode: localOrder.billing_postcode || '',
            country: localOrder.billing_country || '',
            email: localOrder.billing_email || '',
            phone: localOrder.billing_phone || '',
          },
          line_items: localItems.map(it => ({
            id: it.item_id,
            product_id: it.product_id,
            variation_id: it.variation_id,
            name: it.name || '',
            sku: it.sku || '',
            quantity: it.quantity || 1,
            total: String(it.total_num || 0),
            subtotal: String(it.subtotal_num || it.total_num || 0),
            meta_data: it.meta_data && typeof it.meta_data === 'object' ? Object.entries(it.meta_data).map(([key, value]) => ({ key, value })) : [],
          })),
          fee_lines: localFeeLines,
          meta_data: localOrder.meta_data && typeof localOrder.meta_data === 'object' ? Object.entries(localOrder.meta_data).map(([key, value]) => {
            // Skip _fee_lines as it's already extracted
            if (key === '_fee_lines') return null;
            return { key, value };
          }).filter(Boolean) : [],
          customer_note: localOrder.customer_note || '',
          date_created_gmt: localOrder.date_created_gmt,
          pledge_id: localOrder.pledge_id,
        };
        // Set customer note and comments from local DB
        setCustomerNote(localOrder.customer_note || '');
        setOrderComments(localComments);
        console.log('[OrderEditDialog] ✅ Loaded from local DB:', { 
          orderId, 
          status: localOrder.status,
          customer_id: localOrder.customer_id,
          hasItems: localItems.length > 0,
          itemsCount: localItems.length,
          hasBilling: !!localOrder.billing_first_name
        });
      } else {
        console.warn('[OrderEditDialog] Local DB response missing order data');
      }
    } catch (localErr) {
      console.warn('[OrderEditDialog] Local fetch failed, falling back to WooCommerce:', {
        error: localErr?.message || localErr,
        orderId
      });
    }
    if (!src) {
      // LDB-first: every order lives in the local DB (created locally, or backfilled from WC by the
      // sync worker). We do NOT fall back to a realtime WooCommerce read — WooCommerce is batch-only.
      // If the order genuinely isn't local yet, surface that rather than depending on WC being up.
      console.warn('[OrderEditDialog] Order not in local DB; NOT falling back to WooCommerce (Woo is batch-only).');
      setError(`Order ${orderId} isn't in the local database yet. If it was just created, wait a few seconds and reopen.`);
      return null;
    }

    if (src) {
      console.log('[OrderEditDialog] Setting state with loaded order data:', {
        status: src.status,
        customer_id: src.customer_id,
        hasBilling: !!src.billing,
        billingEmail: src.billing?.email,
        itemsCount: src.line_items?.length || 0
      });
          // Status and payment
          setStatus(src.status || '');
          const loadedPaymentMethod = src.payment_method || '';
          setPaymentMethod(loadedPaymentMethod);
          console.log('[OrderEditDialog] Loaded payment_method:', loadedPaymentMethod);
      // Customer + billing
      setCustomer(src.customer_id ? {
        id: src.customer_id,
        first_name: src.billing?.first_name || '',
        last_name: src.billing?.last_name || '',
        email: src.billing?.email || '',
      } : null);
      setBilling({
        first_name: src.billing?.first_name || '',
        last_name: src.billing?.last_name || '',
        address_1: src.billing?.address_1 || '',
        address_2: src.billing?.address_2 || '',
        city: src.billing?.city || '',
        state: src.billing?.state || '',
        postcode: src.billing?.postcode || '',
        country: src.billing?.country || '',
        email: src.billing?.email || '',
        phone: src.billing?.phone || '',
      });
      // Items (with IDs)
      const li = Array.isArray(src.line_items) ? src.line_items : [];
      const mappedItems = li.map((it, idx) => {
        const meta = Array.isArray(it.meta_data) ? it.meta_data : [];
        const attrLabel = meta
          .filter(m => m && typeof m.key === 'string' && !String(m.key).startsWith('_'))
          .map(m => `${m.display_key || m.key}: ${m.display_value || m.value}`)
          .join(', ');
        return {
          key: `${it.id}-${idx}`,
          id: it.id,
          product_id: it.product_id || null,
          variation_id: it.variation_id || null,
          name: it.name || '',
          sku: it.sku || '',
          quantity: it.quantity || 1,
          total: parseFloat(it.total || it.subtotal || 0) || 0,
          variation_label: attrLabel || it.name || '',
        };
      });
      setItems(mappedItems);
      // Store original items for change detection (all 4 fields: product_id, variation_id, quantity, total)
      originalItemsRef.current = mappedItems.map(it => ({
        id: it.id,
        product_id: it.product_id || null,
        variation_id: it.variation_id || null,
        quantity: it.quantity || 1,
        total: parseFloat(it.total) || 0,
      }));
      // Fees (with IDs) - replace all existing fees with loaded fees
      // Filter out fees with 0 total (WooCommerce may keep them even when "removed")
      const fees = Array.isArray(src.fee_lines) ? src.fee_lines.filter(f => {
        const total = parseFloat(f.total || 0);
        return Math.abs(total) > 0.01; // Keep only fees with non-zero total
      }) : [];
      
      // Detect duplicate fees (same amount, similar names indicating processing fees)
      const feeAmounts = new Map(); // amount -> array of fee names
      fees.forEach(f => {
        const total = parseFloat(f.total || 0);
        const normalizedName = (f.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const isProcessingFee = normalizedName.includes('creditcard') || 
                               normalizedName.includes('creditcard') ||
                               normalizedName.includes('processing') ||
                               normalizedName.includes('fee');
        
        if (isProcessingFee && Math.abs(total) > 0.01) {
          if (!feeAmounts.has(total)) {
            feeAmounts.set(total, []);
          }
          feeAmounts.get(total).push(f.name || '');
        }
      });
      
      // Log warning if duplicates detected
      feeAmounts.forEach((names, amount) => {
        if (names.length > 1) {
          console.warn(`[OrderEditDialog] Duplicate processing fees detected for amount $${amount.toFixed(2)}:`, names);
        }
      });
      
      // Clear and replace fee lines (don't merge with existing)
      const mappedFees = fees.map((f, i) => ({
        key: `${f.id || 'new'}-${i}`,
        id: f.id,
        name: f.name || '',
        total: parseFloat(f.total || 0) || 0,
      }));
      setFeeLines(mappedFees); // This replaces the entire array
      // Store original fee lines for change detection
      originalFeeLinesRef.current = mappedFees.map(fl => ({
        id: fl.id,
        name: fl.name || '',
        total: parseFloat(fl.total) || 0,
      }));
      if (process.env.NODE_ENV !== 'production') {
        console.log(`[OrderEditDialog] Loaded ${mappedFees.length} fee lines from ${src.fee_lines ? 'WooCommerce' : 'local DB'}`);
      }
      // Meta fields & customer note
      const meta = Array.isArray(src.meta_data) ? src.meta_data : [];
      const findMeta = (k) => {
        const m = meta.find(m => String(m.key).toLowerCase() === String(k).toLowerCase());
        return m ? (m.value ?? '') : '';
      };
          const td = findMeta('is_tax_deductible');
          setTaxDeductible(String(td).toLowerCase() === 'yes' || String(td).toLowerCase() === 'true');
          setGothram(findMeta('gothram'));
          setFamilyMembers(findMeta('family_members'));
          setSpouse(findMeta('billing_spouse'));
          setTxId(
            String(
              src.transaction_id ||
                findMeta('tx_id') ||
                findMeta('check_number') ||
                findMeta('transaction_id') ||
                findMeta('_transaction_id') ||
                '',
            ),
          );
          const loadedNotes = findMeta('order_notes');
          setNotes(loadedNotes);
          console.log('[OrderEditDialog] Loaded notes from meta_data:', loadedNotes);
          setCustomerNote(src.customer_note || '');
      // Order change - check meta field
      const changeValue = findMeta('order_change') || findMeta('change');
      setOrderChange(changeValue ? formatMoney(changeValue) : '0.00');
      // Pledge ID - check both meta field and direct property
      const pledgeIdFromMeta = findMeta('pledge_id') || findMeta('wc_pledge_id');
      const pledgeIdValue = src.pledge_id || pledgeIdFromMeta || '';
      setSelectedPledgeId(pledgeIdValue ? String(pledgeIdValue) : '');
      // Date
      const srcDate = src.date_created_gmt || src.date_created;
      if (srcDate) {
        const formattedDate = dayjs(srcDate).format('YYYY-MM-DDTHH:mm');
        setOrderDate(formattedDate);
        originalOrderDateRef.current = formattedDate;
      }
    }
    
    return src;
  }, []);

  // Load payment method facet when order id is known
  React.useEffect(() => {
    (async () => {
      if (!order?.id) return;
      try {
        // Fetch payment methods facet
        const res = await get('/ldb/facets/payment-methods', {});
        const arr = Array.isArray(res?.payment_methods) ? res.payment_methods.map(p => p.payment_method) : [];
        setPaymentMethods(arr);
      } catch {
        setPaymentMethods([]);
      }
      try {
        await loadOrderData(order.id);
      } catch {
        // ignore hydration errors, keep existing state
      } finally {
        setInitialLoading(false);
      }
    })();
  }, [order?.id]); // Removed loadOrderData from deps to prevent unnecessary re-runs

  const fetchCustomers = async (q) => {
    if (!q || q.trim().length < 3) { setCustomerOptions([]); return; }
    try {
      setCustLoading(true);
      const res = await get('/ldb/facets/customers', { q });
      setCustomerOptions(Array.isArray(res?.customers) ? res.customers : []);
    } catch {
      setCustomerOptions([]);
    } finally { setCustLoading(false); }
  };

  // Load order comments (private/internal notes) - loaded from same endpoint as order data
  // Comments are loaded in the main useEffect that fetches order details

  // Load pending pledges for customer
  React.useEffect(() => {
    const loadPledges = async () => {
      const custId = customer?.id;
      if (!custId) {
        setPledges([]);
        setSelectedPledgeId('');
        return;
      }
      try {
        setLoadingPledges(true);
        const listRes = await get('/ldb/orders', { customer_id: custId, status: 'pledge-pending', per_page: 100 });
        const baseArr = Array.isArray(listRes?.data) ? listRes.data : [];
        // Enrich each with line_items
        const detailed = await Promise.all(baseArr.map(async (o) => {
          try {
            const det = await get(`/ldb/orders/${o.id}`);
            return { ...o, line_items: Array.isArray(det?.items) ? det.items.map(it => ({
              id: it.item_id || it.id,
              product_id: it.product_id,
              variation_id: it.variation_id,
              name: it.name,
              quantity: it.quantity,
              total: String(it.total_num ?? it.price_num ?? 0),
              meta_data: [],
            })) : [] };
          } catch { return { ...o, line_items: [] }; }
        }));
        setPledges(detailed);
      } catch (e) {
        console.error('[OrderEditDialog] Failed to load pledges:', e);
        setPledges([]);
      } finally {
        setLoadingPledges(false);
      }
    };
    loadPledges();
  }, [customer?.id]);

  // Build pledge label helper (similar to PostPaymentPage)
  const buildPledgeLabel = React.useCallback((pledgeOrder) => {
    if (!pledgeOrder) return '';
    const li = Array.isArray(pledgeOrder.line_items) && pledgeOrder.line_items.length ? pledgeOrder.line_items[0] : null;
    if (!li) return `Order #${pledgeOrder.id}`;
    let attr = '';
    const meta = Array.isArray(li.meta_data) ? li.meta_data : [];
    const attrPairs = meta
      .filter(m => typeof m.key === 'string' && (m.key.startsWith('pa_') || m.key.startsWith('attribute_')) && m.value)
      .map(m => `${m.key.replace(/^attribute_|^pa_/, '')}: ${m.value}`);
    if (attrPairs.length) attr = ` - ${attrPairs.join(', ')}`;
    return `${li.name}${attr}`;
  }, []);

  // Billing + custom meta editable fields
  const [billing, setBilling] = React.useState({
    first_name: order?.billing_first_name || '',
    last_name: order?.billing_last_name || '',
    address_1: order?.billing_address_1 || '',
    address_2: order?.billing_address_2 || '',
    city: order?.billing_city || '',
    state: order?.billing_state || '',
    postcode: order?.billing_postcode || '',
    country: order?.billing_country || '',
    email: order?.billing_email || '',
    phone: order?.billing_phone || '',
  });
  const [taxDeductible, setTaxDeductible] = React.useState(false);
  const [gothram, setGothram] = React.useState('');
  const [familyMembers, setFamilyMembers] = React.useState('');
  const [spouse, setSpouse] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [customerNote, setCustomerNote] = React.useState('');
  const [orderComments, setOrderComments] = React.useState([]);
  const [loadingComments, setLoadingComments] = React.useState(false);
  // Pledge selection
  const [pledges, setPledges] = React.useState([]);
  const [loadingPledges, setLoadingPledges] = React.useState(false);
  const [selectedPledgeId, setSelectedPledgeId] = React.useState('');

  React.useEffect(() => {
    if (!order?.id) return;
    setBilling({
      first_name: order?.billing_first_name || '',
      last_name: order?.billing_last_name || '',
      address_1: order?.billing_address_1 || '',
      address_2: order?.billing_address_2 || '',
      city: order?.billing_city || '',
      state: order?.billing_state || '',
      postcode: order?.billing_postcode || '',
      country: order?.billing_country || '',
      email: order?.billing_email || '',
      phone: order?.billing_phone || '',
    });
    setTaxDeductible(false);
    setGothram('');
    setFamilyMembers('');
    setSpouse('');
    setNotes('');
    setCustomerNote('');
    setOrderComments([]);
    setTxId('');
    setOrderChange('0.00');
  }, [order?.id, order]);

  const fetchProducts = async (q) => {
    try {
      setProdLoading(true);
      const res = await get('/ldb/facets/products', { q });
      const arr = Array.isArray(res?.products) ? res.products.map(p => ({
        ...p,
        price_num: parsePrice(p.price_num ?? p.price),
      })) : [];
      setProductOptions(arr);
    } catch { setProductOptions([]); } finally { setProdLoading(false); }
  };

  // Debounce autocomplete fetches so typing fires ~1 request after a short pause instead of one per
  // keystroke — the biggest source of /ldb call volume during order editing.
  const searchDebounceRef = React.useRef({});
  const debouncedFetchProducts = (v) => {
    clearTimeout(searchDebounceRef.current.prod);
    searchDebounceRef.current.prod = setTimeout(() => fetchProducts(v), 300);
  };
  const debouncedFetchCustomers = (v) => {
    clearTimeout(searchDebounceRef.current.cust);
    searchDebounceRef.current.cust = setTimeout(() => fetchCustomers(v), 300);
  };

  const fetchVariations = async (product_id) => {
    if (!product_id) { setVariationOptions([]); return; }
    try {
      setVarLoading(true);
      const res = await get('/ldb/facets/variations', { product_id: String(product_id) });
      const arr = Array.isArray(res?.variations)
        ? res.variations.map(v => {
            const attrs = Array.isArray(v.attributes)
              ? v.attributes.map(a => `${a.name}:${a.option}`).join(', ')
              : '';
            return {
              ...v,
              attributes_label: attrs,
              price_num: parsePrice(v.price_num ?? v.price),
            };
          })
        : [];
      setVariationOptions(arr);
      // Also store in the map for lookup
      setVariationsByProduct(prev => {
        const newMap = new Map(prev);
        newMap.set(String(product_id), arr);
        return newMap;
      });
    } catch { setVariationOptions([]); } finally { setVarLoading(false); }
  };

  // Fetch variations for all products from seed order.items (before load completes)
  React.useEffect(() => {
    if (!order?.items) return;
    const productIds = Array.from(new Set(
      order.items
        .filter(it => it.product_id && it.variation_id)
        .map(it => String(it.product_id))
    ));
    if (productIds.length === 0) return;
    
    // Fetch variations for all products in parallel
    Promise.all(productIds.map(async (productId) => {
      try {
        const res = await get('/ldb/facets/variations', { product_id: productId });
        const arr = Array.isArray(res?.variations)
          ? res.variations.map(v => {
              const attrs = Array.isArray(v.attributes)
                ? v.attributes.map(a => `${a.name}:${a.option}`).join(', ')
                : '';
              return {
                ...v,
                attributes_label: attrs,
                price_num: parsePrice(v.price_num ?? v.price),
              };
            })
          : [];
        return { productId, variations: arr };
      } catch {
        return { productId, variations: [] };
      }
    })).then(results => {
      const newMap = new Map();
      results.forEach(({ productId, variations }) => {
        newMap.set(productId, variations);
      });
      setVariationsByProduct(newMap);
    });
  }, [order?.items]);

  const selectedAddPrice = () => {
    const variationPrice = parsePrice(selectedVariationForNew?.price_num ?? selectedVariationForNew?.price);
    if (variationPrice != null) return variationPrice;
    return parsePrice(selectedProductForNew?.price_num ?? selectedProductForNew?.price);
  };

  const applyCustomerToBilling = React.useCallback((cust) => {
    if (!cust) return;
    setBilling(prev => ({
      ...prev,
      first_name: cust.billing_first_name || cust.first_name || '',
      last_name: cust.billing_last_name || cust.last_name || '',
      address_1: cust.billing_address_1 || '',
      address_2: cust.billing_address_2 || '',
      city: cust.billing_city || '',
      state: cust.billing_state || '',
      postcode: cust.billing_postcode || '',
      country: cust.billing_country || '',
      email: cust.billing_email || cust.email || '',
      phone: cust.billing_phone || cust.phone || '',
    }));
    if (cust.billing_spouse || cust.spouse_name) {
      setSpouse(cust.billing_spouse || cust.spouse_name || '');
    }
  }, []);

  const loadCustomerDetails = React.useCallback(async (id) => {
    if (!id) return;
    if (customerDetailsLookup.current[id]) {
      applyCustomerToBilling(customerDetailsLookup.current[id]);
      return;
    }
    try {
      const det = await get(`/ldb/customers/${id}`);
      if (det) {
        customerDetailsLookup.current[id] = det;
        applyCustomerToBilling(det);
        setCustomer(prev => {
          if (prev && String(prev.id) === String(id)) {
            return { ...prev, ...det };
          }
          return prev;
        });
      }
    } catch (err) {
      console.error('[OrderEditDialog] Failed to load customer details', err?.message || err);
    }
  }, [applyCustomerToBilling]);

  const addLine = () => {
    if (!selectedProductForNew) return;
    const newItem = {
      key: `new-${Date.now()}`,
      product_id: selectedProductForNew.id,
      variation_id: selectedVariationForNew?.id || null,
      name: selectedProductForNew.name,
      sku: selectedVariationForNew?.sku || selectedProductForNew.sku || '',
      quantity: Math.max(parseInt(newQty, 10) || 1, 1),
      total: parseFloat(newTotal || '0') || 0,
      variation_label: selectedVariationForNew
        ? (selectedVariationForNew.name || (Array.isArray(selectedVariationForNew.attributes)
            ? selectedVariationForNew.attributes.map(a => `${a.name}:${a.option}`).join(', ')
            : '') || '')
        : '',
    };
    setItems(prev => [...prev, newItem]);
    setSelectedProductForNew(null);
    setSelectedVariationForNew(null);
    setProductQuery('');
    setVariationOptions([]);
    setNewQty(1);
    setNewTotal('0.00');
    clearFieldError('items');
  };

  const removeLine = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const clearFieldError = (field) => {
    setFormErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateRequiredFields = () => {
    const nextErrors = {};
    if (!customer?.id) nextErrors.customer = 'Customer is required.';
    if (!String(status || '').trim()) nextErrors.status = 'Status is required.';
    if (!String(paymentMethod || '').trim()) nextErrors.paymentMethod = 'Payment method is required.';
    if (!String(orderDate || '').trim()) nextErrors.orderDate = 'Order date is required.';
    if (!Array.isArray(items) || items.length < 1) nextErrors.items = 'At least one product item is required.';
    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const save = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');
      setSavingMessage('Saving is in progress...');
      if (!validateRequiredFields()) {
        throw new Error('Please fill all required fields before saving.');
      }
      
      // Validate discount doesn't exceed total
      const lineItemsTotal = items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
      const feesTotal = feeLines
        .filter(fl => {
          const isDiscount = fl.name.toLowerCase() === 'discount' || parseFloat(fl.total) < 0;
          return !isDiscount; // Only count non-discount fees
        })
        .reduce((sum, fl) => sum + (parseFloat(fl.total) || 0), 0);
      const maxDiscount = lineItemsTotal + feesTotal;
      
      const discounts = feeLines.filter(fl => {
        const isDiscount = fl.name.toLowerCase() === 'discount' || parseFloat(fl.total) < 0;
        return isDiscount;
      });
      
      for (const discount of discounts) {
        const discountAmount = Math.abs(parseFloat(discount.total) || 0);
        if (discountAmount > maxDiscount) {
          throw new Error(`Discount cannot exceed the total amount of $${maxDiscount.toFixed(2)}.`);
        }
      }
      
      // Check if date has changed (WooCommerce API doesn't allow date changes, so we always use custom endpoint)
      const orderDateOnly = orderDate ? orderDate.slice(0, 10) : '';
      const originalDateOnly = originalOrderDateRef.current ? originalOrderDateRef.current.slice(0, 10) : '';
      const dateChanged = orderDate && orderDateOnly !== originalDateOnly;
      const todayChicago = getChicagoTodayIsoDate();
      const backdatedChange = Boolean(dateChanged && orderDateOnly && orderDateOnly !== todayChicago);
      
      // Build update payload (exclude date_created_gmt - will be handled separately if needed)
      const payload = {};
      const incomingCustomerId = customer?.id ? Number(customer.id) : 0;
      const originalCustomerId = Number(order?.customer_id || order?.customer?.id || 0);
      payload.status = status;
      payload.customer_id = incomingCustomerId || 0;
      if (paymentMethod) payload.payment_method = paymentMethod;
      // Build line items payload - compare all 4 fields: product_id, variation_id, quantity, total
      // If ANY field changed, remove old item and add as new item
      const lineItemsPayload = [];
      const processedItemIds = new Set(); // Track which item IDs we've processed
      const itemsToRemove = new Set(); // Track items to remove (changed or deleted)
      
      // Debug logging
      if (process.env.NODE_ENV !== 'production') {
        console.log('[OrderEditDialog] Save - Current items:', items.map(it => ({ 
          id: it.id, 
          product_id: it.product_id, 
          variation_id: it.variation_id,
          quantity: it.quantity,
          total: it.total
        })));
        console.log('[OrderEditDialog] Save - Original items:', originalItemsRef.current);
      }
      
      // Process current items
      items.forEach(it => {
        if (it.id) {
          // Existing item - compare all 4 fields with original
          const original = originalItemsRef.current.find(orig => String(orig.id) === String(it.id));
          if (original) {
            // Compare all 4 fields
            const productIdChanged = String(original.product_id || '') !== String(it.product_id || '');
            const variationIdChanged = String(original.variation_id || '') !== String(it.variation_id || '');
            const quantityChanged = Math.max(parseInt(original.quantity) || 1, 1) !== Math.max(parseInt(it.quantity) || 1, 1);
            const totalChanged = Math.abs((parseFloat(original.total) || 0) - (parseFloat(it.total) || 0)) > 0.01; // Allow small floating point differences
            
            const anyFieldChanged = productIdChanged || variationIdChanged || quantityChanged || totalChanged;
            
            if (anyFieldChanged) {
              // Any field changed - remove old item and add as new
              if (process.env.NODE_ENV !== 'production') {
                console.log(`[OrderEditDialog] Item ${it.id} changed - removing old and adding new`, {
                  product_id: productIdChanged,
                  variation_id: variationIdChanged,
                  quantity: quantityChanged,
                  total: totalChanged
                });
              }
              processedItemIds.add(String(it.id));
              itemsToRemove.add(it.id);
              // Add as new item (without ID)
              // Validate product_id and variation_id are valid numbers
              const productId = it.product_id ? parseInt(it.product_id, 10) : null;
              const variationId = it.variation_id ? parseInt(it.variation_id, 10) : null;
              
              if (!productId || isNaN(productId) || productId <= 0) {
                throw new Error(`Invalid product ID: ${it.product_id}. Please select a valid product.`);
              }
              
              const newItem = {
                product_id: productId,
                quantity: Math.max(parseInt(it.quantity, 10) || 1, 1),
                total: (parseFloat(it.total) || 0).toFixed(2),
              };
              
              if (variationId && !isNaN(variationId) && variationId > 0) {
                newItem.variation_id = variationId;
              }
              
              lineItemsPayload.push(newItem);
            } else {
              // No changes - but we MUST include it in payload with ID (WooCommerce needs all items)
              processedItemIds.add(String(it.id));
              // Include the unchanged item with its ID so WooCommerce keeps it
              const unchangedItem = {
                id: it.id,
                quantity: Math.max(parseInt(it.quantity, 10) || 1, 1),
                total: (parseFloat(it.total) || 0).toFixed(2),
              };
              
              // Include product_id and variation_id if they exist (not required for updates, but helpful)
              if (it.product_id) {
                const productId = parseInt(it.product_id, 10);
                if (!isNaN(productId) && productId > 0) {
                  unchangedItem.product_id = productId;
                }
              }
              if (it.variation_id) {
                const variationId = parseInt(it.variation_id, 10);
                if (!isNaN(variationId) && variationId > 0) {
                  unchangedItem.variation_id = variationId;
                }
              }
              
              lineItemsPayload.push(unchangedItem);
              if (process.env.NODE_ENV !== 'production') {
                console.log(`[OrderEditDialog] Item ${it.id} unchanged - including in payload`);
              }
            }
          } else {
            // Item has ID but not in original - treat as new (shouldn't happen, but handle it)
            if (process.env.NODE_ENV !== 'production') {
              console.log(`[OrderEditDialog] Item ${it.id} not in original - adding as new`);
            }
            lineItemsPayload.push({
              ...(it.product_id ? { product_id: it.product_id } : {}),
              ...(it.variation_id ? { variation_id: it.variation_id } : {}),
              quantity: Math.max(parseInt(it.quantity, 10) || 1, 1),
              total: (parseFloat(it.total) || 0).toFixed(2),
            });
          }
        } else {
          // New item (no ID) - create new
          if (process.env.NODE_ENV !== 'production') {
            console.log(`[OrderEditDialog] Adding new item: product ${it.product_id}, variation ${it.variation_id}`);
          }
          lineItemsPayload.push({
            ...(it.product_id ? { product_id: it.product_id } : {}),
            ...(it.variation_id ? { variation_id: it.variation_id } : {}),
            quantity: Math.max(parseInt(it.quantity, 10) || 1, 1),
            total: (parseFloat(it.total) || 0).toFixed(2),
          });
        }
      });
      
      // Remove items that exist in original but were not processed (deleted by user)
      if (originalItemsRef.current.length > 0) {
        originalItemsRef.current.forEach(orig => {
          if (orig.id) {
            const origIdStr = String(orig.id);
            if (!processedItemIds.has(origIdStr) && !itemsToRemove.has(orig.id)) {
              // Item was removed by user
              if (process.env.NODE_ENV !== 'production') {
                console.log(`[OrderEditDialog] Removing item ${orig.id} - deleted by user`);
              }
              itemsToRemove.add(orig.id);
            }
          }
        });
      }
      
      // Build final payload: removals first, then additions/updates
      // This ensures WooCommerce removes items before adding new ones to avoid conflicts
      // IMPORTANT: Only include removals with valid IDs (WooCommerce requires id field for removals)
      const removals = Array.from(itemsToRemove)
        .filter(itemId => itemId != null && itemId !== 0 && itemId !== undefined && itemId !== '')
        .map(itemId => {
          const id = typeof itemId === 'number' ? itemId : parseInt(itemId, 10);
          if (isNaN(id) || id <= 0) {
            console.warn(`[OrderEditDialog] Invalid removal item ID: ${itemId}, skipping`);
            return null;
          }
          return {
            id: id,
            quantity: 0
          };
        })
        .filter(r => r !== null); // Remove any null entries
      
      // Filter out any items from lineItemsPayload that are also being removed (avoid duplicates)
      const itemsToKeep = lineItemsPayload.filter(li => {
        // Keep items without IDs (new items)
        if (!li.id) return true;
        // Keep items with IDs that are NOT in itemsToRemove
        const liId = typeof li.id === 'number' ? li.id : parseInt(li.id, 10);
        return !itemsToRemove.has(liId) && !itemsToRemove.has(li.id);
      });
      
      const finalLineItemsPayload = [...removals, ...itemsToKeep];
      
      if (finalLineItemsPayload.length) {
        payload.line_items = finalLineItemsPayload;
        if (process.env.NODE_ENV !== 'production') {
          console.log('[OrderEditDialog] Final line_items payload:', {
            removals: removals.length,
            itemsToKeep: itemsToKeep.length,
            total: finalLineItemsPayload.length,
            items: finalLineItemsPayload.map(li => ({
              id: li.id || 'new',
              product_id: li.product_id,
              variation_id: li.variation_id,
              quantity: li.quantity
            }))
          });
        }
      }
      
      // Build fee lines payload - compare name and total
      // WooCommerce REPLACES the entire fee_lines array with what we send
      // - Unchanged fees: include with ID (WooCommerce keeps them)
      // - Changed fees: include with ID and new values (WooCommerce updates them)
      // - Removed fees: omit from array (WooCommerce deletes them)
      // - New fees: include without ID (WooCommerce creates them)
      // IMPORTANT: Always send fee_lines array to WooCommerce (even if empty) to ensure proper replacement
      const feeLinesPayload = [];
      const processedFeeIds = new Set();
      const feesToRemove = new Set();
      
      // Process all fees that should exist
      feeLines.forEach(fl => {
        if (fl.id) {
          // Existing fee - compare name and total with original
          const original = originalFeeLinesRef.current.find(orig => String(orig.id) === String(fl.id));
          if (original) {
            const nameChanged = String(original.name || '') !== String(fl.name || '');
            const totalChanged = Math.abs((parseFloat(original.total) || 0) - (parseFloat(fl.total) || 0)) > 0.01;
            
            const anyFieldChanged = nameChanged || totalChanged;
            
            if (anyFieldChanged) {
              // Any field changed - update existing fee by keeping the ID
              // WooCommerce will update the fee when we include the ID with new values
              if (process.env.NODE_ENV !== 'production') {
                console.log(`[OrderEditDialog] Fee ${fl.id} changed - updating with ID`, {
                  name: nameChanged,
                  total: totalChanged
                });
              }
              processedFeeIds.add(String(fl.id));
              // Keep the ID and update the values - WooCommerce will update the existing fee
              feeLinesPayload.push({
                id: fl.id,
                name: fl.name || '',
                total: (parseFloat(fl.total) || 0).toFixed(2),
              });
            } else {
              // No changes - but we still need to include it in payload to keep it
              processedFeeIds.add(String(fl.id));
              feeLinesPayload.push({
                id: fl.id,
                name: fl.name || '',
                total: (parseFloat(fl.total) || 0).toFixed(2),
              });
              if (process.env.NODE_ENV !== 'production') {
                console.log(`[OrderEditDialog] Fee ${fl.id} unchanged - keeping in payload`);
              }
            }
          } else {
            // Fee has ID but not in original - treat as new
            feeLinesPayload.push({
              name: fl.name || '',
              total: (parseFloat(fl.total) || 0).toFixed(2),
            });
          }
        } else {
          // New fee (no ID) - create new
          if (process.env.NODE_ENV !== 'production') {
            console.log(`[OrderEditDialog] Adding new fee: ${fl.name}`);
          }
          feeLinesPayload.push({
            name: fl.name || '',
            total: (parseFloat(fl.total) || 0).toFixed(2),
          });
        }
      });
      
      // Remove fees that exist in original but were not processed (deleted by user)
      if (originalFeeLinesRef.current.length > 0) {
        originalFeeLinesRef.current.forEach(orig => {
          if (orig.id) {
            const origIdStr = String(orig.id);
            if (!processedFeeIds.has(origIdStr) && !feesToRemove.has(orig.id)) {
              // Fee was removed by user
              if (process.env.NODE_ENV !== 'production') {
                console.log(`[OrderEditDialog] Removing fee ${orig.id} - deleted by user`);
              }
              feesToRemove.add(orig.id);
            }
          }
        });
      }
      
      // For fees, WooCommerce replaces ALL fee_lines with what we send
      // Always send fee_lines array (even if empty) to ensure WooCommerce replaces all fees
      // This ensures removed fees are actually removed
      payload.fee_lines = feeLinesPayload;
      
      if (process.env.NODE_ENV !== 'production') {
        console.log(`[OrderEditDialog] Fee lines summary:`, {
          sending: feeLinesPayload.length,
          existed: originalFeeLinesRef.current.length,
          removed: feesToRemove.size,
          added: feeLinesPayload.filter(fl => !fl.id).length,
          updated: feeLinesPayload.filter(fl => {
            if (!fl.id) return false;
            const orig = originalFeeLinesRef.current.find(o => String(o.id) === String(fl.id));
            if (!orig) return false;
            return String(orig.name) !== String(fl.name) || Math.abs(orig.total - parseFloat(fl.total)) > 0.01;
          }).length,
          unchanged: feeLinesPayload.filter(fl => {
            if (!fl.id) return false;
            const orig = originalFeeLinesRef.current.find(o => String(o.id) === String(fl.id));
            if (!orig) return false;
            return String(orig.name) === String(fl.name) && Math.abs(orig.total - parseFloat(fl.total)) <= 0.01;
          }).length
        });
      }
      payload.billing = billing;
      payload.tx_id = String(txId || '').trim();
      if (customerNote) payload.customer_note = customerNote;
      payload.meta_data = [
        { key: 'is_tax_deductible', value: taxDeductible ? 'yes' : 'no' },
        { key: 'gothram', value: gothram },
        { key: 'family_members', value: familyMembers },
        { key: 'billing_spouse', value: spouse },
        { key: 'tx_id', value: String(txId || '').trim() },
        { key: 'check_number', value: paymentMethod === 'cheque' ? String(txId || '').trim() : '' },
        { key: 'order_notes', value: notes },
        { key: 'order_change', value: formatMoney(orderChange) },
        { key: ICCT_ORDER_META.SEND_ORDER_EMAILS, value: ICCT_ORDER_META.NO },
      ];
      // Add pledge_id to meta_data if selected
      if (selectedPledgeId) {
        payload.meta_data.push({ key: 'pledge_id', value: String(selectedPledgeId) });
      }
      // Also pass pledge_id directly to backend for local DB storage
      if (selectedPledgeId) {
        payload.pledge_id = parseInt(selectedPledgeId, 10);
      }
      // Don't include date_created_gmt in main payload - will be handled separately if needed
      if (process.env.NODE_ENV !== 'production') {
        try {
          console.log('[OrderEditDialog][save] payload', JSON.stringify(payload, null, 2));
        } catch (err) {
          console.log('[OrderEditDialog][save] payload stringify failed', err);
        }
      }
      
      let updatedOrder = null;

      const wpLineItems = items.map((it) => {
        const pid = parseInt(it.product_id, 10);
        if (!pid || Number.isNaN(pid)) return null;
        const row = {
          product_id: pid,
          quantity: Math.max(1, parseInt(it.quantity, 10) || 1),
          total: (parseFloat(it.total) || 0).toFixed(2),
        };
        const vid = it.variation_id ? parseInt(it.variation_id, 10) : 0;
        if (vid && !Number.isNaN(vid) && vid > 0) row.variation_id = vid;
        return row;
      }).filter(Boolean);
      const wpFees = feeLinesPayload.map((fl) => ({
        name: fl.name || '',
        total: String((parseFloat(fl.total) || 0).toFixed(2)),
      }));
      const finalSt = String(status || '').trim();
      const wpCommitBody = {
        customer_id: incomingCustomerId || 0,
        final_status: finalSt,
        set_paid: isPaidStatus(finalSt),
        payment_date: orderDateOnly || undefined,
        backdated_change: backdatedChange,
        line_items: wpLineItems,
        fee_lines: wpFees,
        billing,
        meta_data: payload.meta_data,
        customer_note: customerNote || '',
      };
      if (paymentMethod) wpCommitBody.payment_method = paymentMethod;
      if (payload.tx_id) wpCommitBody.tx_id = payload.tx_id;
      // Rich items (with names/prices) for the LDB-first local mirror — the minimized WC line_items
      // above drop the display name; the backend strips this before the deferred WooCommerce write.
      wpCommitBody._local_items = items.map((it) => {
        const pid = parseInt(it.product_id, 10);
        if (!pid || Number.isNaN(pid)) return null;
        const vid = it.variation_id ? parseInt(it.variation_id, 10) : 0;
        return {
          product_id: pid,
          variation_id: (vid && !Number.isNaN(vid) && vid > 0) ? vid : 0,
          name: it.name || '',
          sku: it.sku || '',
          quantity: Math.max(1, parseInt(it.quantity, 10) || 1),
          price: parseFloat(it.price) || 0,
          total: parseFloat(it.total) || 0,
        };
      }).filter(Boolean);
      const up = await put(`/api/orders/commit/${order.id}`, wpCommitBody, { timeout: 120000 });
      updatedOrder = up?.order || null;

      if (dateChanged && orderDate && !backdatedChange) {
        try {
          let dateTime = orderDate;
          if (!dateTime.includes('T')) {
            dateTime = `${dateTime}T12:00:00`;
          } else if (dateTime.split('T')[1].split(':').length === 2) {
            dateTime = `${dateTime}:00`;
          }
          await post(`/api/orders/${order.id}/date`, { date_created: dateTime }, { timeout: 90000 });
          console.log('[OrderEditDialog] Date updated via custom endpoint to', dateTime);
        } catch (dateErr) {
          console.error('[OrderEditDialog] Failed to update order date via custom endpoint:', dateErr?.message || dateErr);
          setError(prev => prev ? `${prev}; Date update failed: ${dateErr?.message || 'Unknown error'}` : `Date update failed: ${dateErr?.message || 'Unknown error'}`);
        }
      }

      let freshSavedOrder = null;
      try {
        freshSavedOrder = await loadOrderData(order.id);
      } catch (reloadErr) {
        console.warn('[OrderEditDialog] Fresh reload after save failed:', reloadErr?.message || reloadErr);
      }

      setSaving(false);
      setSavingMessage('');
      setSuccessMessage('');

      const totalFromForm = () => {
        const isum = items.reduce((s, it) => s + (parseFloat(it.total) || 0), 0);
        const fsum = feeLines.reduce((s, fl) => s + (parseFloat(fl.total) || 0), 0);
        return isum + fsum;
      };

      const buildConfirmationSummary = () => {
        const u = freshSavedOrder || updatedOrder || {};
        const b = u.billing || billing;
        const fn = b?.first_name || billing?.first_name || '';
        const ln = b?.last_name || billing?.last_name || '';
        const name = `${fn} ${ln}`.trim();
        const em = b?.email || billing?.email || order?.billing_email || '';
        const customerId = u.customer_id ?? customer?.id ?? null;
        const totalRaw = u.total_num ?? u.total;
        let totalNum = 0;
        if (typeof totalRaw === 'number' && Number.isFinite(totalRaw)) totalNum = totalRaw;
        else if (totalRaw !== undefined && totalRaw !== null && totalRaw !== '') {
          const n = parseFloat(totalRaw);
          totalNum = Number.isNaN(n) ? 0 : n;
        }
        const formT = totalFromForm();
        const apiMissing = totalRaw === undefined || totalRaw === null || totalRaw === '';
        if (apiMissing && Number.isFinite(formT)) {
          totalNum = formT;
        } else if (totalNum === 0 && formT > 0.0001) {
          totalNum = formT;
        }
        return {
          id: u.id ?? order?.id,
          status: u.status ?? status,
          total: totalNum,
          customerId,
          customerLabel: name || em || '—',
        };
      };

      const summary = buildConfirmationSummary();
      navigate(`/orders/${order.id}/updated`, {
        replace: true,
        state: { returnTo, summary, updatedOrder: freshSavedOrder || updatedOrder },
      });
    } catch (e) {
      if (isOrderNotFoundInWc(e)) {
        setOrderNotFoundState(e?.data || {});
      } else {
        setError(e?.message || 'Failed to save order');
      }
      setSaving(false);
      setSavingMessage('');
    }
  };

  // Red circular X close button
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', pb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            onClick={() => navigate(returnTo, { state: { orderEditSaved: true } })}
            aria-label="Back"
            size="large"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">Edit Order #{orderNumber || order?.id}</Typography>
        </Stack>
        {typeof onAddAnotherOrder === 'function' && (
          <Button variant="outlined" onClick={onAddAnotherOrder}>
            Add another order
          </Button>
        )}
      </Paper>
      <Paper sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Stack spacing={2}>
          {savingMessage && (
            <Alert severity="info">
              {savingMessage}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" onClose={() => setSuccessMessage('')}>
              {successMessage}
            </Alert>
          )}
          {error && (
            <Alert severity="error" onClose={() => { setError(''); setOrderNotFoundInWc(false); setWcErrorMessage(''); }}>
              <Box>
                <Typography variant="body2">{error}</Typography>
                {orderNotFoundInWc && order?.id && (
                  <Box sx={{ mt: 1 }}>
                    {wcErrorMessage && wcErrorMessage !== error && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        WooCommerce: {wcErrorMessage}
                      </Typography>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1 }}
                      onClick={async () => {
                        try {
                          setSaving(true);
                          setSavingMessage('Deleting order locally...');
                          await del(`/api/orders/${order.id}/local`);
                          setSaving(false);
                          setSavingMessage('');
                          setError('');
                          setOrderNotFoundInWc(false);
                          setWcErrorMessage('');
                          setSuccessMessage('Order deleted locally successfully');
                          navigate(returnTo, { state: { orderEditSaved: true } });
                        } catch (localErr) {
                          setSaving(false);
                          setSavingMessage('');
                          setError(localErr?.message || 'Failed to delete order locally');
                        }
                      }}
                      disabled={saving}
                    >
                      Delete Locally
                    </Button>
                  </Box>
                )}
              </Box>
            </Alert>
          )}
          <Typography variant="subtitle2">Customer</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
            <Autocomplete
              size="small"
              sx={{ flex: 1, minWidth: 260 }}
              options={customerOptions}
              loading={custLoading}
              value={customer}
              onChange={(_, v) => {
                setCustomer(v);
                clearFieldError('customer');
                if (v?.id) {
                  loadCustomerDetails(v.id);
                }
              }}
              onInputChange={(_, v) => debouncedFetchCustomers(v)}
              getOptionLabel={(o) => {
                if (!o) return '';
                if (typeof o === 'string') return o;
                const name = `${o.first_name || ''} ${o.last_name || ''}`.trim();
                const spouse = o.spouse_name ? ` • ${o.spouse_name}` : '';
                const email = o.email ? ` — ${o.email}` : '';
                return (name || o.email || '').trim() + spouse + email;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Change customer (search 3+ chars)"
                  error={!!formErrors.customer}
                  helperText={formErrors.customer || ''}
                />
              )}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val?.id)}
            />
            <Button
              variant="outlined"
              size="small"
              disabled={!customer?.id}
              onClick={() => customer?.id && navigate(`/devotees/${encodeURIComponent(customer.id)}`)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              View Profile/Orders
            </Button>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start" sx={{ mt: 2 }}>
            {/* Additional Details (left) */}
            <Box sx={{ flex: 1, minWidth: 320 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Additional Details</Typography>
              <Stack spacing={1.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <FormControl size="small" sx={{ minWidth: 200, flex: 1 }} error={!!formErrors.status}>
                  <InputLabel id="order-status-label">Status</InputLabel>
                  <Select
                    labelId="order-status-label"
                    label="Status"
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      clearFieldError('status');
                    }}
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {STATUS_OPTIONS.map(s => (
                      <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
                    ))}
                  </Select>
                  {formErrors.status ? <FormHelperText>{formErrors.status}</FormHelperText> : null}
                </FormControl>
                  <FormControl size="small" sx={{ minWidth: 200, flex: 1 }} error={!!formErrors.paymentMethod}>
                    <InputLabel id="payment-method-label">Payment Method</InputLabel>
                    <Select
                      labelId="payment-method-label"
                      label="Payment Method"
                      value={paymentMethod}
                      onChange={(e) => {
                        setPaymentMethod(e.target.value);
                        clearFieldError('paymentMethod');
                      }}
                    >
                      {paymentMethods.map(pm => (
                        <MenuItem key={pm} value={pm}>{pm}</MenuItem>
                      ))}
                    </Select>
                    {formErrors.paymentMethod ? <FormHelperText>{formErrors.paymentMethod}</FormHelperText> : null}
                  </FormControl>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    label="Order Date"
                    size="small"
                    type="datetime-local"
                    value={orderDate}
                    onChange={(e) => {
                      setOrderDate(e.target.value);
                      clearFieldError('orderDate');
                    }}
                    sx={{ flex: 1 }}
                    InputLabelProps={{ shrink: true }}
                    error={!!formErrors.orderDate}
                    helperText={formErrors.orderDate || ''}
                  />
                  <TextField
                    label="Tx ID / Check #"
                    size="small"
                    value={txId}
                    onChange={(e) => setTxId(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                </Stack>
                {customer?.id && (
                  <FormControl size="small" sx={{ minWidth: 250 }}>
                    <InputLabel id="pledge-select-label">Post Payment to Pledge</InputLabel>
                    <Select
                      labelId="pledge-select-label"
                      label="Post Payment to Pledge"
                      value={selectedPledgeId}
                      onChange={(e) => {
                        const pledgeId = e.target.value;
                        setSelectedPledgeId(pledgeId);
                        
                        // If a pledge is selected, update line items to match pledge's product/variation
                        if (pledgeId) {
                          const selectedPledge = pledges.find(p => String(p.id) === String(pledgeId));
                          if (selectedPledge) {
                            const pledgeLineItem = Array.isArray(selectedPledge.line_items) && selectedPledge.line_items.length > 0 
                              ? selectedPledge.line_items[0] 
                              : null;
                            
                            if (pledgeLineItem) {
                              // Update items to use pledge's product_id and variation_id
                              // But keep existing quantity and total (for partial payments)
                              setItems(prev => {
                                if (prev.length === 0) {
                                  // If no items, create one with pledge's product/variation
                                  return [{
                                    key: `pledge-${pledgeId}-${Date.now()}`,
                                    id: undefined,
                                    product_id: pledgeLineItem.product_id || null,
                                    variation_id: pledgeLineItem.variation_id || null,
                                    name: pledgeLineItem.name || '',
                                    sku: '',
                                    quantity: 1,
                                    total: 0,
                                    variation_label: '',
                                  }];
                                } else {
                                  // Update existing items to match pledge's product/variation
                                  // Keep quantity and total unchanged
                                  return prev.map(item => ({
                                    ...item,
                                    product_id: pledgeLineItem.product_id || null,
                                    variation_id: pledgeLineItem.variation_id || null,
                                    name: pledgeLineItem.name || item.name,
                                    variation_label: '',
                                  }));
                                }
                              });
                            }
                          }
                        }
                      }}
                      disabled={loadingPledges || !pledges.length}
                      sx={{ '& .MuiSelect-select': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }}
                    >
                      <MenuItem value="">None</MenuItem>
                      {loadingPledges && (
                        <MenuItem value="" disabled>Loading pledges...</MenuItem>
                      )}
                      {!loadingPledges && pledges.length === 0 && customer?.id && (
                        <MenuItem value="" disabled>No pending pledges</MenuItem>
                      )}
                      {!loadingPledges && pledges.map(p => {
                        const label = buildPledgeLabel(p);
                        const total = Number(p.total_num || 0);
                        return (
                          <MenuItem key={p.id} value={String(p.id)}>
                            {label ? `${label} • $${total.toFixed(2)}` : `Order #${p.id} • $${total.toFixed(2)}`}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                )}
              </Stack>
              <FormControlLabel control={<Checkbox checked={taxDeductible} onChange={(e) => setTaxDeductible(e.target.checked)} />} label="Is this tax deductible?" />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField label="Gothram" size="small" value={gothram} onChange={(e) => setGothram(e.target.value)} sx={{ flex: 1 }} />
                <TextField label="Spouse" size="small" value={spouse} onChange={(e) => setSpouse(e.target.value)} sx={{ flex: 1 }} />
              </Stack>
              <TextField label="Family members" size="small" value={familyMembers} onChange={(e) => setFamilyMembers(e.target.value)} fullWidth sx={{ mt: 1 }} />
              
              {/* Order Comments (read-only) */}
              {orderComments.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Order History / Private Notes</Typography>
                  <Stack spacing={1}>
                    {orderComments.map((comment, idx) => (
                      <Box key={comment.id || idx} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              {comment.author || 'System'} • {comment.date_created_gmt ? new Date(comment.date_created_gmt).toLocaleString() : ''}
                              {comment.customer_note && <Chip label="Customer Note" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />}
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {comment.note || ''}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
              {/* Resend Receipt moved to dialog actions */}
            </Box>

            {/* Billing (right) */}
            <Box sx={{ flex: 1, minWidth: 360 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Billing</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField label="First name" size="small" value={billing.first_name} onChange={e => setBilling({ ...billing, first_name: e.target.value })} sx={{ flex: 1 }} />
                <TextField label="Last name" size="small" value={billing.last_name} onChange={e => setBilling({ ...billing, last_name: e.target.value })} sx={{ flex: 1 }} />
              </Stack>
              <TextField label="Address 1" size="small" value={billing.address_1} onChange={e => setBilling({ ...billing, address_1: e.target.value })} fullWidth sx={{ mt: 1 }} />
              <TextField label="Address 2" size="small" value={billing.address_2} onChange={e => setBilling({ ...billing, address_2: e.target.value })} fullWidth sx={{ mt: 1 }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                <TextField label="City" size="small" value={billing.city} onChange={e => setBilling({ ...billing, city: e.target.value })} sx={{ flex: 1 }} />
                <TextField label="State" size="small" value={billing.state} onChange={e => setBilling({ ...billing, state: e.target.value })} sx={{ width: 120 }} />
                <TextField label="Postcode" size="small" value={billing.postcode} onChange={e => setBilling({ ...billing, postcode: e.target.value })} sx={{ width: 140 }} />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                <TextField label="Country" size="small" value={billing.country} onChange={e => setBilling({ ...billing, country: e.target.value })} sx={{ width: 160 }} />
                <TextField label="Email" size="small" value={billing.email} onChange={e => setBilling({ ...billing, email: e.target.value })} sx={{ flex: 1 }} />
                <TextField label="Phone" size="small" value={billing.phone} onChange={e => setBilling({ ...billing, phone: e.target.value })} sx={{ width: 180 }} />
              </Stack>
              
              <Divider sx={{ my: 2 }} />
              <TextField label="Customer Note" size="small" value={customerNote} onChange={(e) => setCustomerNote(e.target.value)} fullWidth multiline minRows={2} sx={{ mt: 1 }} helperText="Visible to customer in order emails" />
              <TextField label="Internal Notes/Memo" size="small" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} sx={{ mt: 1 }} helperText="Private notes (not visible to customer)" />
            </Box>
          </Stack>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Items</Typography>
            {formErrors.items ? (
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                {formErrors.items}
              </Typography>
            ) : null}
            <List dense>
              {items.map((it, idx) => (
                <ListItem key={it.key} secondaryAction={<IconButton edge="end" onClick={() => removeLine(idx)}><DeleteIcon /></IconButton>}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%' }}>
                    <Autocomplete
                      size="small"
                      sx={{ minWidth: 260, flex: 1 }}
                      options={productOptions}
                      loading={prodLoading}
                      value={
                        productOptions.find(p => String(p.id) === String(it.product_id)) ||
                        (it.product_id ? { id: it.product_id, name: it.name || '', sku: it.sku || '', price_num: it.total ? parseFloat(it.total) : undefined } : null)
                      }
                      onChange={(_, v) => {
                        setItems(prev => prev.map((x, i) => {
                          if (i !== idx) return x;
                          const price = parsePrice(v?.price_num ?? v?.price);
                          const qtyNum = Math.max(parseFloat(x.quantity) || 1, 1);
                          return {
                            ...x,
                            product_id: v?.id || null,
                            name: v?.name || '',
                            sku: v?.sku || '',
                            variation_id: null,
                            variation_label: '',
                            total: price != null ? Number((price * qtyNum).toFixed(2)) : x.total,
                          };
                        }));
                        if (v?.id) fetchVariations(v.id);
                      }}
                      onInputChange={(_, v) => debouncedFetchProducts(v)}
                      getOptionLabel={(o) => !o ? '' : `${o.name || ''}${o.sku ? ` • ${o.sku}` : ''}${o.id ? ` (${o.id})` : ''}`}
                      renderInput={(params) => <TextField {...params} label="Product" />}
                      isOptionEqualToValue={(opt, val) => String(opt.id) === String(val?.id)}
                    />
                    <Autocomplete
                      size="small"
                      sx={{ minWidth: 240, flex: 1 }}
                      options={variationOptions}
                      loading={varLoading}
                      value={
                        (() => {
                          // First try to find in current variationOptions (for the product being edited)
                          const found = variationOptions.find(v => String(v.id) === String(it.variation_id));
                          if (found) return found;
                          
                          // Then try to find in variationsByProduct map
                          const productVariations = variationsByProduct.get(String(it.product_id)) || [];
                          const foundInMap = productVariations.find(v => String(v.id) === String(it.variation_id));
                          if (foundInMap) return foundInMap;
                          
                          // Fallback: create a placeholder, but try to parse the variation_label to get a better name
                          if (it.variation_id) {
                            let displayName = it.variation_label || '';
                            // If it's in slug format like "pa_abhishekam: one-time-temple-event", extract the readable part
                            if (displayName.includes(': ')) {
                              const parts = displayName.split(': ');
                              if (parts.length > 1) {
                                // Take the part after the colon and format it
                                displayName = parts.slice(1).join(': ')
                                  .split('-')
                                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                  .join(' ');
                              }
                            }
                            return {
                          id: it.variation_id,
                          sku: it.sku || '',
                              name: displayName,
                              attributes_label: displayName,
                            };
                          }
                          return null;
                        })()
                      }
                      onChange={(_, v) => setItems(prev => prev.map((x, i) => {
                        if (i !== idx) return x;
                        const price = parsePrice(v?.price_num ?? v?.price);
                        return {
                          ...x,
                          variation_id: v?.id || null,
                          sku: v?.sku || x.sku,
                          variation_label: v?.name || v?.attributes_label || x.variation_label || '',
                          total: price != null
                            ? Number((price * (Math.max(parseFloat(x.quantity) || 1, 1))).toFixed(2))
                            : x.total,
                        };
                      }))}
                      getOptionLabel={(o) => {
                        if (!o) return '';
                        const name = o.name || '';
                        const attrs = o.attributes_label || (Array.isArray(o.attributes) ? o.attributes.map(a => `${a.name}:${a.option}`).join(', ') : '');
                        const price = o.price_num != null ? ` — $${Number(o.price_num).toFixed(2)}` : '';
                        const base = name || attrs || o.sku || (o.id ? `#${o.id}` : '');
                        const sku = o.sku ? ` • ${o.sku}` : '';
                        return `${base}${sku}${price}`;
                      }}
                      onOpen={() => { 
                        if (it.product_id) {
                          // Check if we already have variations cached
                          const cached = variationsByProduct.get(String(it.product_id));
                          if (cached && cached.length > 0) {
                            setVariationOptions(cached);
                          } else {
                            fetchVariations(it.product_id);
                          }
                        }
                      }}
                      isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                      renderInput={(params) => <TextField {...params} label="Variation" />}
                      disabled={!it.product_id}
                    />
                    <TextField label="Qty" size="small" type="number" value={it.quantity}
                      onChange={(e) => {
                        const newQty = e.target.value;
                        setItems(prev => prev.map((x, i) => {
                          if (i !== idx) return x;
                          // Calculate unit price from current total and quantity
                          const currentQty = Math.max(parseFloat(x.quantity) || 1, 1);
                          const currentTotal = parseFloat(x.total) || 0;
                          // If total is 0, try to get price from product/variation
                          let unitPrice = 0;
                          if (currentTotal > 0 && currentQty > 0) {
                            unitPrice = currentTotal / currentQty;
                          } else {
                            // Try to get price from product or variation
                            const product = productOptions.find(p => String(p.id) === String(x.product_id));
                            const variation = variationOptions.find(v => String(v.id) === String(x.variation_id));
                            const price = parsePrice(variation?.price_num ?? variation?.price ?? product?.price_num ?? product?.price);
                            unitPrice = price || 0;
                          }
                          // Recalculate total with new quantity
                          const newQtyNum = Math.max(parseFloat(newQty) || 1, 1);
                          const newTotal = Number((unitPrice * newQtyNum).toFixed(2));
                          return { ...x, quantity: newQty, total: newTotal };
                        }));
                      }}
                      sx={{ width: 90 }}
                    />
                    <TextField
                      label="Line Total"
                      size="small"
                      type="number"
                      value={formatMoney(it.total)}
                      onChange={(e) => setItems(prev => prev.map((x, i) => i===idx ? { ...x, total: e.target.value } : x))}
                      sx={{ width: 160 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { step: '0.01' }
                      }}
                    />
                  </Stack>
                </ListItem>
              ))}
            </List>

            {/* Fees (CC fee, Discount) */}
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Fees & Discounts</Typography>
            <List dense>
              {feeLines.map((fl, idx) => (
                <ListItem key={fl.key} secondaryAction={<IconButton edge="end" onClick={() => setFeeLines(prev => prev.filter((_, i) => i !== idx))}><DeleteIcon /></IconButton>}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%' }}>
                    <TextField label="Name" size="small" value={fl.name}
                      onChange={(e) => setFeeLines(prev => prev.map((x, i) => i===idx ? { ...x, name: e.target.value } : x))}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Amount"
                      size="small"
                      type="number"
                      value={formatMoney(fl.total)}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        const isDiscount = fl.name.toLowerCase() === 'discount' || parseFloat(fl.total) < 0;
                        
                        // If editing a discount, validate it doesn't exceed the total
                        if (isDiscount) {
                          const newAmount = parseFloat(newValue) || 0;
                          if (newAmount < 0) {
                            // Calculate maximum allowed discount (sum of line items + fees, excluding current discount)
                            const lineItemsTotal = items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
                            const feesTotal = feeLines
                              .filter((f, i) => {
                                if (i === idx) return false; // Exclude current discount being edited
                                const fIsDiscount = f.name.toLowerCase() === 'discount' || parseFloat(f.total) < 0;
                                return !fIsDiscount; // Only count non-discount fees
                              })
                              .reduce((sum, f) => sum + (parseFloat(f.total) || 0), 0);
                            const maxDiscount = lineItemsTotal + feesTotal;
                            
                            if (Math.abs(newAmount) > maxDiscount) {
                              setError(`Discount cannot exceed the total amount of $${maxDiscount.toFixed(2)}.`);
                              return;
                            }
                          }
                        }
                        
                        setError('');
                        setFeeLines(prev => prev.map((x, i) => i===idx ? { ...x, total: newValue } : x));
                      }}
                      sx={{ width: 180 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { step: '0.01' }
                      }}
                    />
                  </Stack>
                </ListItem>
              ))}
            </List>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
              <TextField 
                label="Fee name" 
                size="small" 
                value={newFeeName} 
                onChange={(e) => setNewFeeName(e.target.value)} 
                sx={{ minWidth: 220 }}
                required
                error={false}
                disabled={feeLines.some(fl => {
                  const isDiscount = fl.name.toLowerCase() === 'discount' || fl.total < 0;
                  return !isDiscount; // Disable if any non-discount fee exists
                })}
              />
              <TextField
                label="Amount"
                size="small"
                type="number"
                value={formatMoney(newFeeTotal)}
                onChange={(e) => setNewFeeTotal(e.target.value)}
                sx={{ width: 180 }}
                required
                error={false}
                disabled={feeLines.some(fl => {
                  const isDiscount = fl.name.toLowerCase() === 'discount' || fl.total < 0;
                  return !isDiscount; // Disable if any non-discount fee exists
                })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { step: '0.01' }
                }}
              />
              <Button 
                variant="outlined" 
                onClick={() => {
                  const feeName = newFeeName.trim();
                  const feeAmount = parseFloat(newFeeTotal || '0');
                  if (!feeName || !feeAmount || feeAmount <= 0) {
                    setError('Fee name and amount are required. Amount must be greater than 0.');
                    return;
                  }
                  setError('');
                  setFeeLines(prev => [...prev, { key: `fee-${Date.now()}`, name: feeName, total: feeAmount }]);
                  setNewFeeName(''); 
                  setNewFeeTotal('0.00');
                }}
                disabled={feeLines.some(fl => {
                  const isDiscount = fl.name.toLowerCase() === 'discount' || fl.total < 0;
                  return !isDiscount; // Disable if any non-discount fee exists
                })}
              >
                Add Fee
              </Button>
              <Box sx={{ flex: 1 }} />
              <TextField
                label="Discount amount"
                size="small"
                type="number"
                value={formatMoney(discountAmt)}
                onChange={(e) => setDiscountAmt(e.target.value)}
                sx={{ width: 200 }}
                disabled={feeLines.some(fl => fl.name.toLowerCase() === 'discount' && fl.total < 0)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { step: '0.01' }
                }}
              />
              <Button 
                variant="outlined" 
                onClick={() => {
                  const amt = -(Math.abs(parseFloat(discountAmt || '0')) || 0);
                  if (!amt) {
                    setError('Discount amount must be greater than 0.');
                    return;
                  }
                  
                  // Calculate maximum allowed discount (sum of line items + fees)
                  const lineItemsTotal = items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
                  const feesTotal = feeLines
                    .filter(fl => {
                      const isDiscount = fl.name.toLowerCase() === 'discount' || fl.total < 0;
                      return !isDiscount; // Only count non-discount fees
                    })
                    .reduce((sum, fl) => sum + (parseFloat(fl.total) || 0), 0);
                  const maxDiscount = lineItemsTotal + feesTotal;
                  
                  if (Math.abs(amt) > maxDiscount) {
                    setError(`Discount cannot exceed the total amount of $${maxDiscount.toFixed(2)}.`);
                    return;
                  }
                  
                  setError('');
                  setFeeLines(prev => [...prev, { key: `discount-${Date.now()}`, name: 'Discount', total: amt }]);
                  setDiscountAmt('0.00');
                }}
                disabled={feeLines.some(fl => fl.name.toLowerCase() === 'discount' && fl.total < 0)}
              >
                Add Discount
              </Button>
            </Stack>

            {/* Add new line (below Items and Fees) */}
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Add Product</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
              <Autocomplete
                size="small"
                sx={{ minWidth: 300, flex: 1 }}
                options={productOptions}
                loading={prodLoading}
                value={selectedProductForNew}
                onChange={(_, v) => {
                  setSelectedProductForNew(v);
                  setSelectedVariationForNew(null);
                  fetchVariations(v?.id);
                  const price = parsePrice(v?.price_num ?? v?.price);
                  if (price != null) {
                    const qtyNum = Math.max(parseFloat(newQty) || 1, 1);
                    setNewTotal((price * qtyNum).toFixed(2));
                  } else {
                    setNewTotal('0.00');
                  }
                }}
                onInputChange={(_, v) => { setProductQuery(v); debouncedFetchProducts(v); }}
                getOptionLabel={(o) => !o ? '' : `${o.name || ''}${o.sku ? ` • ${o.sku}` : ''}`}
                renderInput={(params) => <TextField {...params} label="Product" />}
              />
              <Autocomplete
                size="small"
                sx={{ minWidth: 300, flex: 1 }}
                options={variationOptions}
                loading={varLoading}
                value={selectedVariationForNew}
                onChange={(_, v) => {
                  setSelectedVariationForNew(v);
                  const price = parsePrice(v?.price_num ?? v?.price);
                  if (price != null) {
                    const qtyNum = Math.max(parseFloat(newQty) || 1, 1);
                    setNewTotal((price * qtyNum).toFixed(2));
                  }
                }}
                getOptionLabel={(o) => {
                  if (!o) return '';
                  const attrs = o.attributes_label || (Array.isArray(o.attributes) ? o.attributes.map(a => `${a.name}:${a.option}`).join(', ') : '');
                  const price = o.price_num ? ` — $${Number(o.price_num).toFixed(2)}` : '';
                  const base = o.name || attrs || (o.id ? `#${o.id}` : '');
                  const sku = o.sku ? ` • ${o.sku}` : '';
                  return `${base}${sku}${price}`;
                }}
                renderInput={(params) => <TextField {...params} label="Variation (optional)" />}
                disabled={!selectedProductForNew}
              />
              <TextField
                label="Qty"
                size="small"
                type="number"
                value={newQty}
                onChange={(e) => {
                  const val = e.target.value;
                  setNewQty(val);
                  const qtyNum = Math.max(parseFloat(val) || 1, 1);
                  const price = selectedAddPrice();
                  if (price != null) {
                    setNewTotal((price * qtyNum).toFixed(2));
                  }
                }}
                sx={{ width: 100 }}
              />
              <TextField
                label="Line Total"
                size="small"
                type="number"
                value={formatMoney(newTotal)}
                onChange={(e) => setNewTotal(e.target.value)}
                sx={{ width: 200 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { step: '0.01' }
                }}
              />
              <Button startIcon={<AddIcon />} variant="outlined" onClick={addLine}>Add</Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>
      <Paper sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" justifyContent="flex-end" useFlexGap sx={{ gap: 1 }}>
        <Button
          color="error"
          variant="outlined"
          onClick={async () => {
            if (!order?.id) return;
            const ok = window.confirm('Are you sure you want to trash this order? The order will be moved to trash and marked for deletion.');
            if (!ok) return;
            try {
              setError('');
              setSuccessMessage('');
              setSaving(true);
              setSavingMessage('Trashing order...');
              
              // Use dedicated trash endpoint - deletes order in WooCommerce (moves to trash)
              const response = await del(`/api/orders/${order.id}/trash`);
              const updatedOrder = response?.order || response || null;
              
              setSaving(false);
              setSavingMessage('');
              setSuccessMessage('Order trashed successfully');
              navigate(returnTo, { state: { orderEditSaved: true } });
            } catch (err) {
              if (isOrderNotFoundInWc(err)) {
                setOrderNotFoundState(err?.data || {});
              } else {
                setError(err?.message || 'Failed to trash order');
              }
              setSaving(false);
              setSavingMessage('');
            }
          }}
          disabled={saving}
          sx={{ minWidth: 120 }}
        >
          Delete
        </Button>
        <Button 
          color="error" 
          onClick={async () => {
            if (!order?.id) return;
            const ok = window.confirm('Are you sure you want to cancel this order? The order status will be changed to "cancelled".');
            if (!ok) return;
            try {
              setError('');
              setSuccessMessage('');
              setSaving(true);
              setSavingMessage('Cancelling order...');
              
              // Use dedicated cancel endpoint - directly sets status to cancelled
              const response = await put(`/api/orders/${order.id}/cancel`, {});
              const updatedOrder = response?.order || response || null;
              
              setSaving(false);
              setSavingMessage('');
              setSuccessMessage('Order cancelled successfully');
              navigate(returnTo, { state: { orderEditSaved: true } });
            } catch (err) {
              if (isOrderNotFoundInWc(err)) {
                setOrderNotFoundState(err?.data || {});
              } else {
                setError(err?.message || 'Failed to cancel order');
              }
              setSaving(false);
              setSavingMessage('');
            }
          }} 
          disabled={saving}
          sx={{ minWidth: 120 }}
        >
          Cancel Order
        </Button>
        <Button 
          onClick={async () => { 
            try {
              setError('');
              setSuccessMessage('');
              setSendingEmail(true);
              setSavingMessage('Sending email in progress...');
              const emailAddress = sanitizeEmail(billing?.email || order?.billing_email || order?.billing?.email || '');
              if (emailAddress && isInternalNoEmail(emailAddress)) {
                throw new Error("Cannot send receipt: this email is not customer's real email and was internally assigned.");
              }
              await post(`/api/orders/${order.id}/resend-receipt`, {});
              setSendingEmail(false);
              setSavingMessage('');
              setSuccessMessage(`Sending is completed. Email receipt sent to ${emailAddress || 'the customer'}`);
              // Don't auto-dismiss - user will close dialog manually with X button
            } catch (err) {
              setSendingEmail(false);
              setSavingMessage('');
              setError(err?.message || 'Failed to send email receipt');
            }
          }} 
          disabled={saving || sendingEmail}
          sx={{ minWidth: 140 }}
        >
          {sendingEmail ? 'Sending...' : 'Email Receipt'}
        </Button>
        <Button
          onClick={save}
          disabled={saving || initialLoading}
          variant="contained"
          sx={{ minWidth: 120 }}
        >
          {initialLoading ? 'Loading…' : 'Save'}
        </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
